import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { ArrowRight, ArrowLeft, Shield, Zap, Crown, Car, Truck, Ship, Bike, Star } from "lucide-react";
import { ReviewList } from "@/components/ReviewList";
import { useServices } from "@/hooks/use-services";
import { usePublishedReviews } from "@/hooks/use-reviews";
import { ServiceCard } from "@/components/ServiceCard";
import { useTranslation } from "react-i18next";
import vanImage from "@assets/king_wash_van.png";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

type VehicleFilter = 'all' | 'sedan' | 'suv' | 'cuv' | 'motorcycle' | 'van' | 'caravan' | 'boat';

export default function Home() {
  const { data: services } = useServices();
  const { data: allReviews } = usePublishedReviews();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [vehicleFilter, setVehicleFilter] = useState<VehicleFilter>('all');

  const avgRating = allReviews && allReviews.length > 0
    ? allReviews.reduce((sum, r) => sum + (r.rating || 0), 0) / allReviews.length
    : 0;
  const reviewCount = allReviews?.length || 0;

  const filteredServices = useMemo(() => {
    if (!services) return [];
    const nonAddOns = services.filter(s => !s.isAddOn);

    if (vehicleFilter === 'all') return nonAddOns;
    // sedan/suv/cuv/motorcycle - show services with pricing > 0 for selected vehicle type
    return nonAddOns.filter(s =>
      s.vehiclePricing &&
      s.vehiclePricing[vehicleFilter] &&
      s.vehiclePricing[vehicleFilter] > 0
    );
  }, [services, vehicleFilter]);

  const filterOptions = useMemo(() => {
    const allOptions: { value: VehicleFilter; label: string; icon?: typeof Car }[] = [
      { value: 'all', label: t("booking.allVehicles") },
      { value: 'sedan', label: t("booking.sedan"), icon: Car },
      { value: 'cuv', label: t("booking.cuv"), icon: Car },
      { value: 'suv', label: t("booking.suv"), icon: Car },
      { value: 'van', label: t("booking.van"), icon: Truck },
      { value: 'motorcycle', label: t("booking.motorcycle"), icon: Bike },
      { value: 'caravan', label: t("booking.caravan"), icon: Truck },
      { value: 'boat', label: t("booking.boat"), icon: Ship },
    ];

    if (!services) return [{ value: 'all' as const, label: t("booking.allVehicles") }];

    const nonAddOns = services.filter(s => !s.isAddOn);

    // Check which vehicle types have services with pricing > 0
    const availableTypes = new Set<VehicleFilter>();

    (['sedan', 'suv', 'cuv', 'motorcycle', 'van', 'caravan', 'boat'] as const).forEach(type => {
      const hasServices = nonAddOns.some(s =>
        s.vehiclePricing &&
        s.vehiclePricing[type] &&
        s.vehiclePricing[type] > 0
      );
      if (hasServices) availableTypes.add(type);
    });

    // Return "All" + only available vehicle types
    return allOptions.filter(option =>
      option.value === 'all' || availableTypes.has(option.value)
    );
  }, [services, t]);

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative bg-background py-20 lg:py-36 xl:py-44 overflow-x-hidden">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-4 items-center">
            <div className="max-w-md animate-fade-in-up">
              {avgRating > 0 && (
                <div
                  className="inline-flex items-center gap-2 rounded-full border border-secondary/40 px-4 py-2 mb-6"
                  data-testid="hero-rating"
                >
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        className={cn(
                          "h-4 w-4",
                          s <= Math.round(avgRating)
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-gray-300",
                        )}
                      />
                    ))}
                  </div>
                  <span className="text-lg font-semibold">{avgRating.toFixed(1)}</span>
                  <span className="text-sm text-muted-foreground">
                    ({reviewCount} {reviewCount === 1 ? t("reviews.single") : t("reviews.plural")})
                  </span>
                </div>
              )}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display leading-tight mb-4">
                <span className="italic font-bold">{t("hero.slogan1")}</span>
                <br />
                <span className="italic font-bold">{t("hero.slogan2")}</span>
              </h1>
              <p className="text-muted-foreground mb-8 text-lg leading-relaxed">
                {t("hero.description")}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link href="/booking">
                  <Button size="lg" className="px-6">
                    {t("hero.bookNow")}
                  </Button>
                </Link>
                <Link href="/services">
                  <Button variant="outline" size="lg" className="px-6">
                    {t("hero.ourServices")}
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative lg:justify-self-end animate-fade-in-right">
              <img
                src={vanImage}
                alt="King Wash service van"
                className="w-full max-w-full h-auto object-contain drop-shadow-xl lg:max-w-2xl lg:scale-110 xl:max-w-3xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-card relative">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 font-display">{t("features.title")}</h2>
            <p className="text-muted-foreground">{t("features.subtitle")}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Crown className="w-6 h-6" />,
                title: t("features.royalService"),
                desc: t("features.royalServiceDesc")
              },
              {
                icon: <Shield className="w-6 h-6" />,
                title: t("features.paintProtection"),
                desc: t("features.paintProtectionDesc")
              },
              {
                icon: <Zap className="w-6 h-6" />,
                title: t("features.expressService"),
                desc: t("features.expressServiceDesc")
              }
            ].map((feature, i) => (
              <div
                key={i}
                className="p-8 rounded-3xl bg-muted border border-border hover:bg-card hover:shadow-xl hover:shadow-secondary/5 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-secondary/10 rounded-2xl flex items-center justify-center text-secondary mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
            <div className="max-w-xl">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 font-display">{t("services.title")}</h2>
              <p className="text-muted-foreground">{t("services.subtitle")}</p>
            </div>
            <Link href="/services">
              <Button variant="outline" className="group">
                {t("services.viewAll")}
                {isRTL ? (
                  <ArrowLeft className="me-2 w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                ) : (
                  <ArrowRight className="ms-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                )}
              </Button>
            </Link>
          </div>

          {/* Vehicle Type Filter Pills */}
          <div className="flex flex-wrap justify-center gap-2 mb-10 overflow-x-hidden">
            {filterOptions.map((option) => (
              <Badge
                key={option.value}
                variant={vehicleFilter === option.value ? "default" : "outline"}
                className={cn(
                  "cursor-pointer px-3 py-2 text-sm transition-all whitespace-nowrap",
                  vehicleFilter === option.value && "bg-primary text-primary-foreground"
                )}
                onClick={() => setVehicleFilter(option.value)}
                data-testid={`home-filter-${option.value}`}
              >
                {option.icon && <option.icon className="w-4 h-4 me-1.5" />}
                {option.label}
              </Badge>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredServices.slice(0, 6).map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} vehicleFilter={vehicleFilter} />
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-24 bg-card border-y border-border/50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-16 font-display">{t("reviews.title")}</h2>
          <ReviewList />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-muted/50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 font-display">{t("faq.title")}</h2>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {(t("faq.items", { returnObjects: true }) as { question: string; answer: string }[]).map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-border">
                  <AccordionTrigger className="text-left py-4 hover:no-underline" data-testid={`faq-question-${index}`}>
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

    </div>
  );
}
